import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, Check, Clock, Loader2, Plus, Send, Trash2, UserMinus, UserPlus, Users, type LucideIcon } from 'lucide-react'

interface SchemeOption {
  id: string
  name: string
  code: string | null
}

interface ExistingMember {
  membership_id: string
  person_id: string
  full_name: string
  email: string | null
  phone: string | null
  member_status: string | null
  enrollment_date: string | null
}

interface ContextResponse {
  token: {
    expires_at: string
    sent_to_name: string | null
    sent_to_email: string
  }
  company: {
    id: string
    company_name: string
  }
  contact: {
    id: string
    name: string
    email: string | null
    position: string | null
  }
  schemes: SchemeOption[]
  members: ExistingMember[]
}

interface JoinerDraft {
  id: string
  full_name: string
  email: string
  phone: string
  date_of_birth: string
  residential_address: string
  employment_start_date: string
}

type LeaverDrafts = Record<string, string>

const emptyJoiner = (): JoinerDraft => ({
  id: crypto.randomUUID(),
  full_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  residential_address: '',
  employment_start_date: '',
})

export function MemberChangeFormPage() {
  const { token = '' } = useParams()
  const [context, setContext] = useState<ContextResponse | null>(null)
  const [selectedSchemeId, setSelectedSchemeId] = useState('')
  const [joiners, setJoiners] = useState<JoinerDraft[]>([])
  const [leavers, setLeavers] = useState<LeaverDrafts>({})
  const [loading, setLoading] = useState(true)
  const [membersLoading, setMembersLoading] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const draftKey = selectedSchemeId ? `elab-member-change:${token}:${selectedSchemeId}` : ''

  useEffect(() => {
    loadContext()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    if (!selectedSchemeId) return
    loadContext(selectedSchemeId)
    const stored = localStorage.getItem(`elab-member-change:${token}:${selectedSchemeId}`)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setJoiners(Array.isArray(parsed.joiners) ? parsed.joiners : [])
        setLeavers(parsed.leavers && typeof parsed.leavers === 'object' ? parsed.leavers : {})
      } catch {
        setJoiners([])
        setLeavers({})
      }
    } else {
      setJoiners([])
      setLeavers({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchemeId])

  useEffect(() => {
    if (!draftKey || submitted) return
    setSavingDraft(true)
    const timer = window.setTimeout(() => {
      localStorage.setItem(draftKey, JSON.stringify({ joiners, leavers }))
      setSavingDraft(false)
    }, 250)
    return () => window.clearTimeout(timer)
  }, [draftKey, joiners, leavers, submitted])

  const selectedScheme = context?.schemes.find((scheme) => scheme.id === selectedSchemeId)
  const selectedLeavers = useMemo(() => {
    return Object.entries(leavers)
      .filter(([, leavingDate]) => leavingDate)
      .map(([membershipId, leavingDate]) => ({
        membership_id: membershipId,
        leaving_date: leavingDate,
        member: context?.members.find((member) => member.membership_id === membershipId),
      }))
  }, [context?.members, leavers])

  const cleanJoiners = joiners.filter((joiner) => joiner.full_name.trim() && joiner.employment_start_date)
  const hasChanges = cleanJoiners.length > 0 || selectedLeavers.length > 0

  async function loadContext(schemeId?: string) {
    setError(null)
    setMembersLoading(Boolean(schemeId))
    if (!schemeId) setLoading(true)
    try {
      const params = new URLSearchParams({ token })
      if (schemeId) params.set('scheme_id', schemeId)
      const response = await fetch(`/api/groups/member-change-token?${params.toString()}`)
      const json = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(json.error || 'Unable to load this form')
      setContext(json)
      if (!schemeId && json.schemes?.length === 1) setSelectedSchemeId(json.schemes[0].id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load this form')
    } finally {
      setLoading(false)
      setMembersLoading(false)
    }
  }

  const addJoiner = () => setJoiners((current) => [...current, emptyJoiner()])
  const removeJoiner = (id: string) => setJoiners((current) => current.filter((joiner) => joiner.id !== id))
  const updateJoiner = (id: string, field: keyof JoinerDraft, value: string) => {
    setJoiners((current) => current.map((joiner) => joiner.id === id ? { ...joiner, [field]: value } : joiner))
  }

  const setLeavingDate = (membershipId: string, value: string) => {
    setLeavers((current) => {
      const next = { ...current }
      if (value) next[membershipId] = value
      else delete next[membershipId]
      return next
    })
  }

  async function handleSubmit() {
    if (!selectedSchemeId || !hasChanges) return
    setSubmitting(true)
    setError(null)
    try {
      const response = await fetch('/api/groups/member-change-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          scheme_id: selectedSchemeId,
          joiners: cleanJoiners.map((joiner) => ({
            full_name: joiner.full_name,
            email: joiner.email,
            phone: joiner.phone,
            date_of_birth: joiner.date_of_birth,
            residential_address: joiner.residential_address,
            employment_start_date: joiner.employment_start_date,
          })),
          leavers: selectedLeavers.map(({ membership_id, leaving_date }) => ({ membership_id, leaving_date })),
        }),
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(json.error || 'Unable to submit changes')
      if (draftKey) localStorage.removeItem(draftKey)
      setSubmitted(true)
      setJoiners([])
      setLeavers({})
      await loadContext(selectedSchemeId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit changes')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <PublicShell>
        <div className="flex min-h-[360px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </PublicShell>
    )
  }

  if (error && !context) {
    return (
      <PublicShell>
        <StatusPanel tone="error" title="This link cannot be opened" message={error} />
      </PublicShell>
    )
  }

  return (
    <PublicShell>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <aside className="space-y-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-orange-600">Member changes</p>
              <h1 className="mt-2 text-2xl font-semibold leading-tight text-slate-950">{context?.company.company_name}</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Add joiners or mark employees as leaving for the selected subgroup. Changes update Employee Lab immediately.
              </p>
              <div className="mt-4 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                <Clock className="h-4 w-4 text-slate-400" />
                Expires {formatDate(context?.token.expires_at)}
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <label className="block text-sm font-semibold text-slate-800">Subgroup</label>
              <select
                value={selectedSchemeId}
                onChange={(event) => setSelectedSchemeId(event.target.value)}
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
              >
                <option value="">Choose subgroup</option>
                {context?.schemes.map((scheme) => (
                  <option key={scheme.id} value={scheme.id}>{scheme.name}</option>
                ))}
              </select>
              {selectedScheme && (
                <p className="mt-3 text-xs text-slate-500">
                  Only staff in {selectedScheme.name} are shown.
                </p>
              )}
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">Change basket</h2>
              <div className="mt-3 space-y-2 text-sm">
                <BasketRow icon={UserPlus} label="Joiners" value={cleanJoiners.length} />
                <BasketRow icon={UserMinus} label="Leavers" value={selectedLeavers.length} />
              </div>
              <div className="mt-4 text-xs text-slate-500">
                {savingDraft ? 'Saving draft...' : 'Draft saved locally as you work.'}
              </div>
            </section>
          </aside>

          <main className="space-y-5">
            {submitted && (
              <StatusPanel tone="success" title="Changes submitted" message="Employee Lab has been notified and the member records have been updated." />
            )}
            {error && <StatusPanel tone="error" title="Check the form" message={error} />}

            {!selectedSchemeId ? (
              <section className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
                <Users className="mx-auto h-10 w-10 text-slate-300" />
                <h2 className="mt-4 text-lg font-semibold text-slate-900">Choose a subgroup to start</h2>
                <p className="mt-2 text-sm text-slate-500">You will only see staff in the subgroup you select.</p>
              </section>
            ) : (
              <>
                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-950">Add joiners</h2>
                      <p className="mt-1 text-sm text-slate-500">Full name and employment start date are required.</p>
                    </div>
                    <button onClick={addJoiner} className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
                      <Plus className="h-4 w-4" /> Add joiner
                    </button>
                  </div>

                  <div className="mt-5 space-y-4">
                    {joiners.length === 0 ? (
                      <p className="rounded-xl bg-slate-50 px-4 py-4 text-sm text-slate-500">No joiners added yet.</p>
                    ) : joiners.map((joiner, index) => (
                      <div key={joiner.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-slate-800">Joiner {index + 1}</h3>
                          <button onClick={() => removeJoiner(joiner.id)} className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-red-500" aria-label="Remove joiner">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          <FormField label="Full name *" value={joiner.full_name} onChange={(value) => updateJoiner(joiner.id, 'full_name', value)} />
                          <FormField label="Employment start date *" type="date" value={joiner.employment_start_date} onChange={(value) => updateJoiner(joiner.id, 'employment_start_date', value)} />
                          <FormField label="Email" type="email" value={joiner.email} onChange={(value) => updateJoiner(joiner.id, 'email', value)} />
                          <FormField label="Phone" value={joiner.phone} onChange={(value) => updateJoiner(joiner.id, 'phone', value)} />
                          <FormField label="Date of birth" type="date" value={joiner.date_of_birth} onChange={(value) => updateJoiner(joiner.id, 'date_of_birth', value)} />
                          <FormField label="Residential address" value={joiner.residential_address} onChange={(value) => updateJoiner(joiner.id, 'residential_address', value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950">Mark leavers</h2>
                    <p className="mt-1 text-sm text-slate-500">Set a final/leaving date beside any current staff member leaving this subgroup.</p>
                  </div>

                  <div className="mt-5 overflow-hidden rounded-xl border border-slate-200">
                    {membersLoading ? (
                      <div className="flex items-center justify-center py-10 text-slate-500">
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading staff...
                      </div>
                    ) : context?.members.length === 0 ? (
                      <p className="px-4 py-5 text-sm text-slate-500">No current staff are listed for this subgroup.</p>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {context?.members.map((member) => (
                          <div key={member.membership_id} className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_180px] sm:items-center">
                            <div className="min-w-0">
                              <div className="font-medium text-slate-900">{member.full_name}</div>
                              <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                                {member.email && <span>{member.email}</span>}
                                {member.phone && <span>{member.phone}</span>}
                                {member.member_status && <span>{member.member_status}</span>}
                              </div>
                            </div>
                            <input
                              type="date"
                              value={leavers[member.membership_id] || ''}
                              onChange={(event) => setLeavingDate(member.membership_id, event.target.value)}
                              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
                              aria-label={`Leaving date for ${member.full_name}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-950">Review and submit</h2>
                      <p className="mt-1 text-sm text-slate-500">
                        {hasChanges
                          ? `${cleanJoiners.length} joiner${cleanJoiners.length === 1 ? '' : 's'} and ${selectedLeavers.length} leaver${selectedLeavers.length === 1 ? '' : 's'} ready.`
                          : 'Add at least one joiner or leaver before submitting.'}
                      </p>
                    </div>
                    <button
                      onClick={handleSubmit}
                      disabled={!hasChanges || submitting}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Submit changes
                    </button>
                  </div>
                </section>
              </>
            )}
          </main>
        </div>
      </div>
    </PublicShell>
  )
}

function PublicShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-100 text-slate-950">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <img src="/employee-lab-email-logo.png" width="154" height="58" alt="Employee Lab logo" className="h-11 w-auto" />
          <div className="hidden items-center gap-2 rounded-full bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 sm:flex">
            <ShieldIcon /> Secure employer link
          </div>
        </div>
      </header>
      {children}
    </div>
  )
}

function ShieldIcon() {
  return <Check className="h-3.5 w-3.5" />
}

function BasketRow({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: number }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
      <span className="flex items-center gap-2 text-slate-600"><Icon className="h-4 w-4 text-orange-500" /> {label}</span>
      <span className="font-semibold text-slate-950">{value}</span>
    </div>
  )
}

function FormField({
  label,
  type = 'text',
  value,
  onChange,
}: {
  label: string
  type?: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
      />
    </label>
  )
}

function StatusPanel({ tone, title, message }: { tone: 'success' | 'error'; title: string; message: string }) {
  const success = tone === 'success'
  return (
    <div className={`rounded-2xl border p-4 ${success ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
      <div className="flex items-start gap-3">
        {success ? <Check className="mt-0.5 h-5 w-5 text-emerald-600" /> : <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />}
        <div>
          <h2 className={`font-semibold ${success ? 'text-emerald-950' : 'text-red-950'}`}>{title}</h2>
          <p className={`mt-1 text-sm ${success ? 'text-emerald-800' : 'text-red-800'}`}>{message}</p>
        </div>
      </div>
    </div>
  )
}

function formatDate(value?: string) {
  if (!value) return ''
  return new Date(value)
    .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .replaceAll('/', '-')
}
